import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutlined";
import { Autocomplete, Box, Button, CircularProgress, IconButton, type SxProps, Tooltip, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type ReactNode, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemTabs } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import ColorfulRole from "../../../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { type DropdownItem, SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { SystemDatePickerPopup } from "../../../../components/UIToolkit/Popups/SystemDatePickerPopup";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { EmploymentType, type Nurse, type NurseWithExternalData } from "../../../../utils/models";
import { getEmploymentTypeDisplayName, isAgencyNurse } from "../../../../utils/utilFunctions";
import { CommonHeader } from "../util/constants";
import { AgencyDropdown, NurseEmploymentTypeDropdown, NurseRolesDropdown, NurseTrainingStatusDropdown, NurseWageTypeDropdown } from "./Dropdowns";
import { useApplyPrimaryJob } from "./useApplyPrimaryJob";
import { useProfessionalInfoState } from "./useProfessionalInfoState";

const mediumWidth = "260px";

type DescriptionAndDetailsBoxProps = {
	description: string;
	details: ReactNode;
	sx?: SxProps;
};
const DescriptionAndDetailsBox: FC<DescriptionAndDetailsBoxProps> = ({ description, details, sx }) => {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				justifyContent: "space-between",
				gap: "4px",
				fontWeight: 700,
				fontSize: {
					xs: "12px",
					md: "20px",
				},

				lineHeight: "100%",
				color: "#4D5579",
				...sx,
			}}
		>
			<Typography
				sx={{
					fontWeight: 500,
					fontSize: {
						xs: "12px",
						md: "16px",
					},
					lineHeight: "100%",
					color: "#4D557980",
				}}
			>
				{description}
			</Typography>
			{details}
		</Box>
	);
};

const rowGap = "16px";

type ProfessionalInfoProps = {
	nurse?: NurseWithExternalData;
	isEditable: boolean;
	setNurseData: Dispatch<SetStateAction<Nurse>>;
	showExtendedDemoGraphics?: boolean;
};
const descriptionBoxSx = {
	width: {
		xs: "100%",
		md: "auto",
	},
} as const satisfies SxProps;

export const ProfessionalInfo: FC<ProfessionalInfoProps> = ({ nurse, isEditable, setNurseData, showExtendedDemoGraphics }) => {
	const { userInfo } = useAuth();
	const { t } = useTranslation();
	const {
		selectedRole,
		onRoleChange,
		selectedContractType,
		onContractTypeChange,
		selectedWageType,
		onWageTypeChange,
		wageValue,
		onWageChange,
		selectedTrainingStatusType,
		onTrainingStatusChange,
		canBeReliefChargeValue,
		onCanBeReliefChargeChange,
		permanentShiftManagerValue,
		onPermanentShiftManagerChange,
		excludeFromPpdValue,
		onExcludeFromPpdChange,
		ignoreFromScheduleValue,
		onIgnoreFromScheduleChange,
		doNotDisturbCallingValue,
		onDoNotDisturbCallingChange,
		autoApproveEligibleValue,
		onAutoApproveEligibleChange,
		availableRoles,
		prnShiftsInCycle,
		onPrnShiftsInCycleChange,
		prnWeekendShiftsInCycle,
		onPrnWeekendShiftsInCycleChange,
		isWageHiddenForCurrentUser,
		baseWage,
		fetchingAgencyWage,
		selectedAgency,
		onAgencyChange,
		agencies,
		createAgency,
		unionValue,
		isUnionMember,
		onUnionMemberChange,
		onUnionNameChange,
		existingUnions,
		existingUnionsLoading,
		selectedHomeDepartmentId,
		onHomeDepartmentChange,
		additionalFacilities,
		allFacilitiesFromSameHospital,
		onAdditionalFacilitiesChange,
		isSupportUser,
		userDepartments,
		availableJobs,
		selectedJobIds,
		onJobsChange,
		recommendedJobs,
		showJobRecommendation,
		onApplyRecommendedJobs,
		onSetPrimaryJob,
		showJobReminderDialog,
		setShowJobReminderDialog,
	} = useProfessionalInfoState({ nurse, setNurseData, isEditable });
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const isJobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const isAutoApproveEnabled = useFeatureFlag(FEATURE_FLAGS.AutoApproveShifts);
	const [showIgnoreWarning, setShowIgnoreWarning] = useState(false);
	const [currentTab, setCurrentTab] = useState<"skills" | "employment" | "facilities" | "settings">("employment");
	const {
		anchorRef: applyJobAnchorRef,
		pickerOpen: applyJobPickerOpen,
		setPickerOpen: setApplyJobPickerOpen,
		loading: applyJobLoading,
		selectedDate: selectedApplyDate,
		handleDateChange: handleApplyDateChange,
		handleSave: handleSaveApplyJob,
	} = useApplyPrimaryJob(nurse?.id);

	const handleIgnoreFromScheduleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setShowIgnoreWarning(true);
		} else {
			onIgnoreFromScheduleChange(event);
		}
	};

	const isViewMode = !isEditable && !!nurse;
	const tabGridColumns = nurse?.employmentType === EmploymentType.PRN ? "repeat(6, 1fr)" : "repeat(5, 1fr)";
	const tabs: SystemTab<typeof currentTab>[] = [
		{ label: "Employment", value: "employment", icon: <WorkOutlineIcon fontSize="small" /> },
		{ label: "Skills", value: "skills", icon: <SchoolOutlinedIcon fontSize="small" /> },
		{ label: "Facilities", value: "facilities", icon: <ApartmentOutlinedIcon fontSize="small" /> },
		{ label: "Settings", value: "settings", icon: <SettingsOutlinedIcon fontSize="small" /> },
	];
	const nurseHomeDepartmentDropdownItems =
		nurse?.departments
			.map((deptId) => userInfo.departments.find((dept) => deptId === dept.department_id))
			.filter((dept) => !!dept)
			.map<DropdownItem<string>>((dept) => ({
				id: dept.department_id,
				label: dept.display_name,
				value: dept.department_id,
				selectedLabel: dept.display_name,
			})) || [];

	// Get display names for additional facilities
	const getAdditionalFacilitiesDisplayNames = (facilityIds: string[]): string => {
		if (!facilityIds || facilityIds.length === 0) return "--";
		return facilityIds.map((id) => nurse?.departmentDisplayNames?.[id] ?? userDepartments.find((d) => d.department_id === id)?.display_name ?? id).join(", ");
	};

	// dropdown items for all facilities from same hospital (for edit mode)
	const allFacilitiesDropdownItems = allFacilitiesFromSameHospital.map<DropdownItem<string>>((dept) => ({
		id: dept.department_id,
		label: dept.display_name,
		value: dept.department_id,
		selectedLabel: dept.display_name,
	}));

	return (
		<Box
			data-testid="profile-professional-section"
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				rowGap: rowGap,
				justifyContent: "space-between",
				pb: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
					flexWrap: "wrap",
					gap: "8px",
				}}
			>
				<CommonHeader>{t("professional.info")}</CommonHeader>
				<SystemTabs tabs={tabs} currentTab={currentTab} onTabChange={setCurrentTab} dataTestId="profile-professional-info-tabs" />
			</Box>

			{currentTab === "employment" && (
				<Box
					sx={{
						display: { xs: "flex", md: "grid" },
						flexDirection: { xs: "row", md: "initial" },
						flexWrap: "wrap",
						gridTemplateColumns: tabGridColumns,
						gridAutoRows: "auto",
						columnGap: { md: "20px" },
						rowGap: { xs: "8px", md: isViewMode ? "26px" : rowGap },
						width: "100%",
					}}
				>
					<DescriptionAndDetailsBox
						sx={descriptionBoxSx}
						description={t("role")}
						details={
							isViewMode ? (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										flexWrap: "wrap",
										gap: "4px",
									}}
								>
									<ColorfulRole role={nurse?.role} />
									{nurse?.role}
								</Box>
							) : (
								<NurseRolesDropdown
									data-testid="profile-role-dropdown"
									onChange={onRoleChange}
									selectedRole={selectedRole ?? undefined}
									items={availableRoles}
									error={isEditable && !selectedRole}
								/>
							)
						}
					/>
					<DescriptionAndDetailsBox
						description={t("job")}
						details={
							isViewMode ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									<Box>
										{nurse?.jobs?.map((j, i) => (
											<Typography key={j.id} component="span" sx={{ fontWeight: 700 }}>
												{j.displayName}
												{i === 0 && (nurse?.jobs?.length ?? 0) > 1 ? ` (${t("primary")})` : ""}
												{i < (nurse?.jobs?.length ?? 0) - 1 ? ", " : ""}
											</Typography>
										)) || "--"}
									</Box>
									{(nurse?.jobs?.length ?? 0) > 0 && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
											<Tooltip title={t("apply.primary.job.to.shifts.description")}>
												<span>
													<Button
														ref={applyJobAnchorRef}
														size="small"
														variant="outlined"
														disabled={applyJobLoading}
														onClick={() => setApplyJobPickerOpen(true)}
														sx={{ fontSize: "12px", textTransform: "none" }}
													>
														{t("apply.primary.job.to.shifts")}
														{selectedApplyDate ? `: ${selectedApplyDate.toFormat("MMM d, yyyy")}` : ""}
													</Button>
												</span>
											</Tooltip>
											{selectedApplyDate && (
												<Button
													size="small"
													variant="contained"
													disabled={applyJobLoading}
													onClick={handleSaveApplyJob}
													sx={{ fontSize: "12px", textTransform: "none" }}
												>
													{applyJobLoading ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
													{t("save")}
												</Button>
											)}
										</Box>
									)}
									<SystemDatePickerPopup
										open={applyJobPickerOpen}
										anchorEl={applyJobAnchorRef.current}
										value={selectedApplyDate}
										onChange={(date) => handleApplyDateChange(date as DateTime | null)}
										onClose={() => setApplyJobPickerOpen(false)}
										datePickerToolbarTitle={t("apply.primary.job.to.shifts")}
									/>
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
									{showJobRecommendation && (
										<InlineHint
											dataTestId="profile-job-recommendation"
											sx={{ maxWidth: mediumWidth }}
											actionPlacement="below"
											action={
												<SystemButton
													buttonVariant="borderless"
													size="small"
													noFullWidthMobile
													data-testid="profile-job-recommendation-apply"
													onClick={onApplyRecommendedJobs}
												>
													{t("profile.jobs.recommendation.apply")}
												</SystemButton>
											}
										>
											{t("profile.jobs.recommendation", { jobs: recommendedJobs.map((job) => job.displayName).join(", ") })}
										</InlineHint>
									)}
									<SystemDropdown
										formControlSx={{ width: mediumWidth }}
										selectSx={{ minHeight: "40px" }}
										id="nurse-jobs-selector"
										multiple
										fullWidth
										minimalSelection={1}
										fieldLabel={t("job")}
										items={availableJobs.map((j) => ({
											value: j.id,
											label: j.displayName,
											selectedLabel: j.displayName,
										}))}
										selectedItems={selectedJobIds}
										onChangeMultiple={onJobsChange}
									/>
									{selectedJobIds.length > 1 && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
											<Typography sx={{ fontWeight: 500, fontSize: { xs: "12px", md: "16px" }, lineHeight: "100%", color: "#4D557980", whiteSpace: "nowrap" }}>
												{t("primary.job")}
											</Typography>
											<SystemDropdown
												formControlSx={{ width: 160 }}
												selectSx={{ minHeight: "32px", fontSize: "13px" }}
												id="nurse-primary-job-selector"
												items={selectedJobIds.map((jobId) => {
													const job = availableJobs.find((j) => j.id === jobId);
													return { value: jobId, label: job?.displayName ?? jobId };
												})}
												selectedItem={selectedJobIds[0] ?? ""}
												onChange={(value) => onSetPrimaryJob(value as string)}
											/>
										</Box>
									)}
								</Box>
							)
						}
					/>
					<DescriptionAndDetailsBox
						sx={descriptionBoxSx}
						description={t("contract.type")}
						details={
							isViewMode ? (
								getEmploymentTypeDisplayName(nurse?.employmentType)
							) : (
								<NurseEmploymentTypeDropdown data-testid="profile-contract-type-dropdown" onChange={onContractTypeChange} selectedType={selectedContractType} />
							)
						}
					/>

					{nurse && isAgencyNurse(EmploymentType[selectedContractType]) && (
						<DescriptionAndDetailsBox
							sx={descriptionBoxSx}
							description={t("agency")}
							details={
								isViewMode ? (
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<span>{nurse?.agencyName || "--"}</span>
										{nurse?.agencyProfileUrls?.map((url) => (
											<Tooltip key={url} title="View agency profile">
												<IconButton
													component="a"
													href={url}
													target="_blank"
													rel="noopener noreferrer"
													size="small"
													sx={{ p: 0.25 }}
													aria-label="View agency profile"
												>
													<OpenInNewIcon sx={{ fontSize: 14 }} />
												</IconButton>
											</Tooltip>
										))}
									</Box>
								) : (
									<AgencyDropdown selectedAgency={selectedAgency} onChange={onAgencyChange} agencies={agencies} onCreateAgency={createAgency} />
								)
							}
						/>
					)}

					{!isWageHiddenForCurrentUser && (
						<>
							<DescriptionAndDetailsBox
								sx={{ width: { xs: "50%", md: "auto" } }}
								description={t("wage.type")}
								details={
									isViewMode ? (
										baseWage?.wageType
									) : (
										<NurseWageTypeDropdown dataTestId="profile-wage-type-dropdown" selectedType={selectedWageType} onChange={onWageTypeChange} />
									)
								}
							/>

							<DescriptionAndDetailsBox
								sx={{ width: { xs: "50%", md: "auto" } }}
								description={t("wage")}
								details={
									fetchingAgencyWage ? (
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<CircularProgress size={20} />
										</Box>
									) : isViewMode ? (
										<Box component="span" data-testid="profile-wage-display">{`$${wageValue}`}</Box>
									) : (
										<Tooltip title={!selectedWageType ? "Make sure to select a wage type before entering a wage value" : undefined}>
											<Box>
												<SystemValidationTextField
													dataTestId="profile-wage-input"
													disabled={!selectedWageType}
													inputType={"number"}
													inputSx={{ width: { xs: "100%", md: mediumWidth } }}
													onChange={onWageChange}
													value={wageValue}
													reserveHeightForHelperText={false}
												/>
											</Box>
										</Tooltip>
									)
								}
							/>
						</>
					)}
					{nurse?.employmentType === EmploymentType.PRN && (
						<>
							<DescriptionAndDetailsBox
								description={t("number.of.prn.shifts")}
								details={
									isViewMode ? (
										`${nurse?.prnShiftsInCycle ?? 0}`
									) : (
										<SystemValidationTextField
											inputType={"number"}
											onChange={onPrnShiftsInCycleChange}
											value={prnShiftsInCycle}
											reserveHeightForHelperText={false}
										/>
									)
								}
							/>
							<DescriptionAndDetailsBox
								description={t("number.of.prn.weekend.shifts")}
								details={
									isViewMode ? (
										`${nurse?.prnWeekendShiftsInCycle ?? 0}`
									) : (
										<SystemValidationTextField
											inputType={"number"}
											onChange={onPrnWeekendShiftsInCycleChange}
											value={prnWeekendShiftsInCycle}
											reserveHeightForHelperText={false}
										/>
									)
								}
							/>
						</>
					)}
					{showExtendedDemoGraphics && (
						<DescriptionAndDetailsBox description={t("reliability.score")} details={`${((nurse?.reliabilityScore ?? 0) * 100).toFixed(0)}/100`} />
					)}
					{isViewMode ? (
						<DescriptionAndDetailsBox description={t("union")} details={nurse?.union || "--"} />
					) : (
						<Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
							<SystemSwitch checked={isUnionMember} onChange={onUnionMemberChange} label={t("union")} loading={existingUnionsLoading} />
							<Autocomplete
								freeSolo
								disabled={!isUnionMember}
								options={existingUnions ?? []}
								value={unionValue}
								onInputChange={onUnionNameChange}
								renderInput={(params) => (
									<SystemValidationTextField
										placeholder={t("union.name")}
										injectedProps={params}
										inputSx={{ width: mediumWidth }}
										reserveHeightForHelperText={false}
									/>
								)}
							/>
						</Box>
					)}
				</Box>
			)}

			{currentTab === "skills" && (
				<Box
					sx={{
						display: { xs: "flex", md: "grid" },
						flexDirection: { xs: "row", md: "initial" },
						flexWrap: "wrap",
						gridTemplateColumns: { md: "repeat(auto-fill, minmax(260px, 1fr))" },
						gridAutoRows: "auto",
						alignItems: "start",
						columnGap: { md: "20px" },
						rowGap: { xs: "8px", md: isViewMode ? "26px" : rowGap },
						width: "100%",
					}}
				>
					<DescriptionAndDetailsBox
						description={t("training.level")}
						details={
							isViewMode ? (
								nurse?.trainingStatus
							) : (
								<NurseTrainingStatusDropdown onChange={onTrainingStatusChange} selectedTrainingStatus={selectedTrainingStatusType} />
							)
						}
					/>
					{isViewMode ? (
						<DescriptionAndDetailsBox description={t("can.be.shift.manager")} details={t(nurse?.canBeReliefCharge ? "yes" : "no")} />
					) : (
						<SystemSwitch
							data-testid="profile-shift-manager-switch"
							checked={canBeReliefChargeValue}
							onChange={onCanBeReliefChargeChange}
							label={t("can.be.shift.manager")}
						/>
					)}
					{isViewMode ? (
						<DescriptionAndDetailsBox description={t("permanent.shift.manager")} details={t(nurse?.isShiftManager ? "yes" : "no")} />
					) : (
						<SystemSwitch
							data-testid="profile-permanent-shift-manager-switch"
							checked={permanentShiftManagerValue}
							onChange={onPermanentShiftManagerChange}
							label={t("permanent.shift.manager")}
						/>
					)}
				</Box>
			)}

			{currentTab === "settings" && (
				<Box
					sx={{
						display: { xs: "flex", md: "grid" },
						flexDirection: { xs: "row", md: "initial" },
						flexWrap: "wrap",
						gridTemplateColumns: tabGridColumns,
						gridAutoRows: "auto",
						columnGap: { md: "20px" },
						rowGap: { xs: "8px", md: isViewMode ? "26px" : rowGap },
						width: "100%",
					}}
				>
					{!isJobManagementEnabled &&
						(isViewMode ? (
							<DescriptionAndDetailsBox description={"Exclude from PPD/HPPD"} details={t(nurse?.excludeFromPpd ? "yes" : "no")} />
						) : (
							<SystemSwitch
								data-testid="profile-exclude-ppd-switch"
								checked={excludeFromPpdValue}
								onChange={onExcludeFromPpdChange}
								label={"Exclude from PPD/HPPD"}
							/>
						))}
					{isViewMode ? (
						<Tooltip title="Removes this employee from future scheduling in this department. Clears upcoming shifts, time-off records, and shift preferences, and pauses the HRIS sync for this employee across all departments. Their past worked shifts are preserved. This action is reversible.">
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<DescriptionAndDetailsBox description={t("ignore.from.schedule")} details={t(nurse?.ignoreFromSchedule ? "yes" : "no")} />
								<InfoOutlinedIcon sx={{ fontSize: 16, color: "#4D557980" }} />
							</Box>
						</Tooltip>
					) : (
						<Tooltip title="Removes this employee from future scheduling in this department. Clears upcoming shifts, time-off records, and shift preferences, and pauses the HRIS sync for this employee across all departments. Their past worked shifts are preserved. This action is reversible.">
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<SystemSwitch checked={ignoreFromScheduleValue} onChange={handleIgnoreFromScheduleToggle} label={t("ignore.from.schedule")} />
								<InfoOutlinedIcon sx={{ fontSize: 16, color: "#4D557980" }} />
							</Box>
						</Tooltip>
					)}
					{isCallOffEnabled &&
						(isViewMode ? (
							<DescriptionAndDetailsBox description={"Do Not Disturb Calling"} details={t(nurse?.doNotDisturbCalling ? "yes" : "no")} />
						) : (
							<SystemSwitch checked={doNotDisturbCallingValue} onChange={onDoNotDisturbCallingChange} label={"Do Not Disturb Calling"} />
						))}
					{isAutoApproveEnabled &&
						(isViewMode ? (
							<DescriptionAndDetailsBox description={t("profile.autoApproveShifts")} details={t(autoApproveEligibleValue ? "yes" : "no")} />
						) : (
							<SystemSwitch
								data-testid="profile-auto-approve-eligible-switch"
								checked={autoApproveEligibleValue}
								onChange={onAutoApproveEligibleChange}
								label={t("profile.autoApproveShifts")}
							/>
						))}
				</Box>
			)}

			{currentTab === "facilities" && (
				<Box
					sx={{
						display: { xs: "flex", md: "grid" },
						flexDirection: { xs: "row", md: "initial" },
						flexWrap: "wrap",
						gridTemplateColumns: { md: "repeat(auto-fill, minmax(260px, 1fr))" },
						gridAutoRows: "auto",
						alignItems: "start",
						columnGap: { md: "20px" },
						rowGap: { xs: "8px", md: isViewMode ? "26px" : rowGap },
						width: "100%",
					}}
				>
					<DescriptionAndDetailsBox
						sx={descriptionBoxSx}
						description={t("home.facility")}
						details={
							isViewMode ? (
								(selectedHomeDepartmentId &&
									(nurse?.departmentDisplayNames?.[selectedHomeDepartmentId] ??
										userDepartments.find((d) => d.department_id === selectedHomeDepartmentId)?.display_name ??
										selectedHomeDepartmentId)) ||
								"--"
							) : (
								<SystemDropdown
									items={nurseHomeDepartmentDropdownItems}
									selectedItem={selectedHomeDepartmentId}
									onChange={onHomeDepartmentChange}
									formControlSx={{
										width: { xs: "100%", md: "auto" },
										minWidth: { xs: "100%", md: "300px" },
									}}
									showFilter
									maxMenuHeight={400}
								/>
							)
						}
					/>
					<DescriptionAndDetailsBox
						sx={descriptionBoxSx}
						description={t("additional.facilities")}
						details={
							isViewMode || !isSupportUser ? (
								getAdditionalFacilitiesDisplayNames(additionalFacilities)
							) : (
								<SystemDropdown
									multiple
									items={allFacilitiesDropdownItems}
									selectedItems={additionalFacilities}
									onChangeMultiple={onAdditionalFacilitiesChange}
									formControlSx={{
										width: { xs: "100%", md: "auto" },
										minWidth: { xs: "100%", md: "300px" },
									}}
									showFilter
									maxMenuHeight={400}
								/>
							)
						}
					/>
				</Box>
			)}

			<SystemDialog
				open={showIgnoreWarning}
				onClose={() => setShowIgnoreWarning(false)}
				title={t("ignore.from.schedule.warning.title")}
				body={t("ignore.from.schedule.warning.body")}
				onConfirm={() => {
					onIgnoreFromScheduleChange({ target: { checked: true } } as React.ChangeEvent<HTMLInputElement>);
					setShowIgnoreWarning(false);
				}}
				cancel={t("cancel")}
				confirm={t("confirm")}
			/>
			<SystemDialog
				open={showJobReminderDialog}
				onClose={() => setShowJobReminderDialog(false)}
				title={t("role.updated.title")}
				body={t("role.updated.job.reminder")}
				onConfirm={() => setShowJobReminderDialog(false)}
				confirm={t("close")}
				hideCancel
			/>
		</Box>
	);
};
